import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'; // Import CheckboxChangeEvent
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { TaskClosingCodesTemplate } from '../constants/TaskClosingCodesTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useClosingCodesService from '../hooks/services/useClosingCodesService';
import { ruLocale } from '../lang/ruLocale';
import { BaseFilter, PageRequest, TaskClosingCode } from '../types/interfaces';
import TaskClosingCodeCard from '../ui/TaskClosingCodeCard';
import { clearFilter } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function TaskClosingCodes() {
  const [taskClosingCodes, setTaskClosingCodes] = useState<TaskClosingCode[]>(
    []
  );
  const [filtredTaskClosingCodes, setFiltredTaskClosingCodes] = useState<
    TaskClosingCode[]
  >([]); // Filtered list
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [taskClosingCodeToDelete, setTaskClosingCodeToDelete] = useState<
    string | number | null
  >(null);
  const [selectedTaskClosingCodes, setSelectedTaskClosingCodes] =
    useState<TaskClosingCode>(TaskClosingCodesTemplate);
  const [showDeleted, setShowDeleted] = useState<boolean>(false); // State to toggle showing deleted codes
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskClosingCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shortName: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const taskClosingCodeFilterRef = useRef<HTMLInputElement>(null);
  const shortNameFilterRef = useRef<HTMLInputElement>(null);
  const [totalCount, setTotalCount] = useState(1);
  const toast = useRef<Toast>(null);
  const taskClosingCodeCardRef = useRef<null | { handleCancel: () => void }>(
    null
  );

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const isSelectedIdExist = isSelected(selectedTaskClosingCodes);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const closingCodesService = useClosingCodesService();
  const getClosingCodesCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const filtersForApi: PageRequest = {
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(
        Object.keys(filters).map((x): BaseFilter => {
          const f = filters[x];
          return {
            field: x,
            value: (f as DataTableFilterMetaData).value,
            operator: (f as DataTableFilterMetaData).matchMode
          };
        })
      )
    };

    const serviceResult = closingCodesService.getAll(filtersForApi);
    serviceResult.result.then((r) => {
      if (r) {
        setTaskClosingCodes(r.itemsList);
        setLoading(false);
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);
      }
    });
    return () => serviceResult.cancel();
  }, [closingCodesService, filters, multiSortMeta]);

  useEffect(() => {
    const cancel = getClosingCodesCallback();
    return () => cancel();
  }, [getClosingCodesCallback]);

  useEffect(() => {
    // Update the filtered list whenever taskClosingCodes or showDeleted changes
    setFiltredTaskClosingCodes(
      showDeleted
        ? taskClosingCodes
        : taskClosingCodes.filter((code) => !code.isDeleted)
    );
  }, [taskClosingCodes, showDeleted]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const handleShowDeletedChange = (e: CheckboxChangeEvent) => {
    const value = e.checked; // Use the checked property from CheckboxChangeEvent
    if (typeof value === 'boolean') setShowDeleted(value);
  };

  const addButtonHandle = () => {
    setSelectedTaskClosingCodes(TaskClosingCodesTemplate);
    setDialogVisible(true);
  };

  const editButtonHandle = () =>  {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите шифр закрытия наряда для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  }

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setTaskClosingCodeToDelete(selectedTaskClosingCodes.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите шифр закрытия наряда для удаления',
        life: 3000
      });
    }
  }
  const handleDeleteConfirmation = () => {
    handleDelete(taskClosingCodeToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedClosingCode: TaskClosingCode
  ) => {
    const result =
      await closingCodesService.addOrUpdateWithError(updatedClosingCode)
        ?.result;
    getClosingCodesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) await closingCodesService.delete(parseInt(id.toString()))?.result;
    setSelectedTaskClosingCodes(TaskClosingCodesTemplate);
    getClosingCodesCallback();
  };

  const handleAdd = async (taskClosingCode: TaskClosingCode) => {
    const result =
      await closingCodesService.addOrUpdateWithError(taskClosingCode)?.result;
    getClosingCodesCallback();
    return result;
  };

  const handleRestore = async (id: number) => {
    await closingCodesService.restore(id)?.result;
    getClosingCodesCallback();
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideTaskClosingCode = () => {
    if (taskClosingCodeCardRef.current !== null) {
      taskClosingCodeCardRef.current.handleCancel();
    }
  };

  const header = (  
    <TablePageHeader
      tableName="taskClosingCodes"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
      customButtonsLeft={[          
        <span className="flex align-items-center gap-2" key="taskClosingCodes">
          <Checkbox
            inputId="showDeleted"
            onChange={handleShowDeletedChange}
            checked={showDeleted}
          />
          <label
            htmlFor="showDeleted"
            className="p-checkbox-label font-normal"
          >
            Отображать удалённые
          </label>
        </span>
      ]}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="taskClosingCode-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Шифр закрытия наряда"
        visible={dialogVisible}
        style={{
          minWidth: '600px',
          width: '600px',
          minHeight: '332px',
          height: '332px'
        }}
        maximizable
        modal
        onHide={onHideTaskClosingCode}
      >
        <VisibilityWrapper code="closing-code-card__roles">
          <TaskClosingCodeCard
            taskClosingCodeProps={selectedTaskClosingCodes}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            onRestore={handleRestore}
            closeDialog={closeDialog}
            ref={taskClosingCodeCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="шифр закрытия наряда"
        entityName={selectedTaskClosingCodes?.taskClosingCode}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedTaskClosingCodes!}
        onSelectionChange={(e) => setSelectedTaskClosingCodes(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedTaskClosingCodes(e.data as TaskClosingCode);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={filtredTaskClosingCodes} // Use filtered data
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Шифры закрытия нарядов не найдены. "
        onRowReorder={(e) => setTaskClosingCodes(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        globalFilterFields={['taskClosingCode']}
        onFilter={onFiltersChange}
        footer={
          <div className="p-paginator p-component p-paginator-bottom justify-content-end" style={{
            height: 60
          }}>
            <span>{`Всего: ${filtredTaskClosingCodes.length > 0 ? totalCount : 0}`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="isDeleted"
          field="isDeleted"
          body={(rowData) => (
            <>
              {rowData.isDeleted && (
                <Tag severity="danger" className="ml-1 danger" key={rowData.id}>
                  Удалён
                </Tag>
              )}
            </>
          )}
          style={{ width: '3%', minWidth: '65px' }}
        />
        <Column
          columnKey="taskClosingCode"
          sortable
          field="taskClosingCode"
          header="Полное название шифра закрытия наряда"
          filter
          onFilterClear={() =>
            clearFilter(
              'taskClosingCode',
              filters,
              setFilters,
              taskClosingCodeFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={taskClosingCodeFilterRef}
                onChange={handleInputChange('taskClosingCode')}
                placeholder="Поиск по полным названиям шифров закрытия нарядов"
              />
            </div>
          }
          style={{ width: '50%' }}
        />
        <Column
          columnKey="shortName"
          sortable
          field="shortName"
          header="Короткое название"
          filter
          onFilterClear={() =>
            clearFilter('shortName', filters, setFilters, shortNameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={shortNameFilterRef}
                onChange={handleInputChange('shortName')}
                placeholder="Поиск по коротким названиям шифров закрытия нарядов"
              />
            </div>
          }
          style={{ width: '33%' }}
        />
      </DataTable>
    </div>
  );
}
